import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import {
  Loader2, Box, Users, TrendingUp, FileText, Globe,
  Lock, Award, BarChart3, Target, Clock, Activity, Zap, AlertTriangle
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function HealthGauge({ value, status, color }) {
  const pct = Math.min((value / 8) * 100, 100);
  const colors = { green: "from-emerald-400 to-emerald-600", yellow: "from-amber-400 to-amber-500", orange: "from-orange-400 to-orange-500", red: "from-red-400 to-red-500" };
  const bgColors = { green: "bg-emerald-50 border-emerald-200", yellow: "bg-amber-50 border-amber-200", orange: "bg-orange-50 border-orange-200", red: "bg-red-50 border-red-200" };
  const textColors = { green: "text-emerald-700", yellow: "text-amber-700", orange: "text-orange-700", red: "text-red-700" };

  return (
    <div className={`rounded-xl border p-5 ${bgColors[color]}`} data-testid="health-gauge">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[9px] uppercase tracking-wider text-[#999] font-semibold">Term Sheets / Deal</div>
          <div className="flex items-baseline gap-2">
            <span className={`text-4xl font-black ${textColors[color]}`}>{value}</span>
            <span className="text-sm text-[#888] font-medium">avg</span>
          </div>
        </div>
        <div className={`px-3 py-1.5 rounded-full text-xs font-bold ${bgColors[color]} ${textColors[color]} border`}>
          {status === "healthy" && <><Activity className="h-3 w-3 inline mr-1" />Healthy</>}
          {status === "growing" && <><TrendingUp className="h-3 w-3 inline mr-1" />Growing</>}
          {status === "warming" && <><Clock className="h-3 w-3 inline mr-1" />Warming</>}
          {status === "cold" && <><AlertTriangle className="h-3 w-3 inline mr-1" />Cold</>}
        </div>
      </div>
      {/* Gauge Bar */}
      <div className="relative h-3 rounded-full bg-white/60 border border-black/5 overflow-hidden">
        <motion.div initial={{ width: 0 }} animate={{ width: `${Math.max(pct, 3)}%` }} transition={{ duration: 1, ease: "easeOut" }}
          className={`h-full rounded-full bg-gradient-to-r ${colors[color]}`} />
        {/* Target zone markers */}
        <div className="absolute top-0 left-[62.5%] h-full w-px bg-emerald-800/30" title="5 ts/deal" />
        <div className="absolute top-0 left-[100%] h-full w-px bg-emerald-800/30" title="8 ts/deal" />
      </div>
      <div className="flex justify-between mt-1.5 text-[9px] text-[#999]">
        <span>0</span>
        <span className="font-semibold text-emerald-600">Target: 5-8</span>
        <span>8+</span>
      </div>
    </div>
  );
}

export default function MarketplaceMetrics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem("ic_token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/loan-box/marketplace/metrics`, { headers });
      setData(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-emerald-700" /></div>;
  if (!data) return <div className="text-center py-8 text-sm text-[#888]">No marketplace data available.</div>;

  const lb = data.loan_boxes || {};
  const match = data.matching || {};
  const mp = data.marketplace || {};
  const comp = data.competition || {};
  const vis = data.visibility || {};
  const topLenders = data.top_lenders || [];

  const tierBadge = (tier) => {
    if (tier === "preferred") return "bg-gold/20 text-gold-dark border-gold/30";
    if (tier === "active") return "bg-blue-100 text-blue-700 border-blue-200";
    return "bg-gray-100 text-gray-600 border-gray-200";
  };

  return (
    <div className="space-y-5" data-testid="marketplace-metrics">
      {/* Row 1: Key Health Gauge + Top KPIs */}
      <div className="grid lg:grid-cols-3 gap-4">
        {/* Health Gauge — the most important metric */}
        <HealthGauge value={comp.avg_ts_per_deal || 0} status={comp.health_status || "cold"} color={comp.health_color || "red"} />

        {/* Core KPIs */}
        <div className="grid grid-cols-2 gap-3 lg:col-span-2">
          {[
            { label: "Active Lenders (30d)", value: mp.active_lenders_30d, icon: Users, color: "text-blue-600", bg: "bg-blue-50" },
            { label: "Deal Match Rate", value: `${match.deal_match_rate || 0}%`, icon: Target, color: "text-purple-600", bg: "bg-purple-50" },
            { label: "Avg Response Time", value: comp.avg_response_hours != null ? `${comp.avg_response_hours}h` : "N/A", icon: Clock, color: "text-amber-600", bg: "bg-amber-50" },
            { label: "Funded Rate", value: `${mp.funded_rate || 0}%`, icon: Zap, color: "text-emerald-600", bg: "bg-emerald-50" },
            { label: "Active Loan Boxes", value: lb.active, icon: Box, color: "text-emerald-700", bg: "bg-emerald-50" },
            { label: "Avg Lenders/Deal", value: match.avg_matches_per_deal, icon: TrendingUp, color: "text-gold-dark", bg: "bg-gold/10" },
          ].map((s, i) => (
            <motion.div key={s.label} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              className={`rounded-xl border border-cream-warm ${s.bg} p-3`} data-testid={`mp-kpi-${s.label.toLowerCase().replace(/[ ()]/g, '-')}`}>
              <div className="flex items-center gap-2 mb-0.5">
                <s.icon className={`h-3.5 w-3.5 ${s.color}`} />
                <span className="text-[9px] uppercase tracking-wider text-[#999] font-semibold">{s.label}</span>
              </div>
              <div className={`text-xl font-bold ${s.color}`}>{s.value}</div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Row 2: Competition + Distribution */}
      <div className="grid lg:grid-cols-2 gap-4">
        {/* Competition Panel */}
        <div className="rounded-xl border border-cream-warm bg-white p-5" data-testid="mp-competition">
          <h4 className="text-xs font-bold text-[#444] uppercase tracking-wider mb-4 flex items-center gap-2">
            <BarChart3 className="h-4 w-4 text-gold-dark" /> Lender Competition
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
            <div className="rounded-xl bg-cream border border-cream-warm p-3 text-center">
              <div className="text-xl font-bold text-purple-600">{mp.total_term_sheets}</div>
              <div className="text-[9px] text-[#999] uppercase tracking-wider">Total Quotes</div>
            </div>
            <div className="rounded-xl bg-cream border border-cream-warm p-3 text-center">
              <div className="text-xl font-bold text-blue-600">{mp.total_interests}</div>
              <div className="text-[9px] text-[#999] uppercase tracking-wider">Interests</div>
            </div>
            <div className="rounded-xl bg-cream border border-cream-warm p-3 text-center">
              <div className="text-xl font-bold text-emerald-600">{comp.deals_with_ts}</div>
              <div className="text-[9px] text-[#999] uppercase tracking-wider">Deals Quoted</div>
            </div>
          </div>
          <div className="flex items-center gap-3 rounded-lg bg-cream border border-cream-warm p-3">
            <Target className="h-4 w-4 text-purple-500" />
            <div>
              <div className="text-sm font-semibold text-[#333]">Max {comp.max_ts_per_deal || 0} quotes on a single deal</div>
              <div className="text-xs text-[#888]">{match.total_qualified_matches} total qualified matches across {match.total_deals_matched} deals</div>
            </div>
          </div>
        </div>

        {/* Visibility Breakdown */}
        <div className="rounded-xl border border-cream-warm bg-white p-5" data-testid="mp-visibility">
          <h4 className="text-xs font-bold text-[#444] uppercase tracking-wider mb-4 flex items-center gap-2">
            <Globe className="h-4 w-4 text-gold-dark" /> Deal Visibility
          </h4>
          <div className="space-y-2.5">
            {[
              { key: "open_market", label: "Open Market", icon: Globe, count: vis.open_market || 0, color: "bg-emerald-400" },
              { key: "preferred", label: "Preferred Only", icon: Award, count: vis.preferred || 0, color: "bg-blue-400" },
              { key: "exclusive", label: "Exclusive", icon: Lock, count: vis.exclusive || 0, color: "bg-gold" },
              { key: "unset", label: "No Visibility Set", icon: Globe, count: vis.unset || 0, color: "bg-gray-300" },
            ].map((v) => {
              const total = (vis.open_market || 0) + (vis.preferred || 0) + (vis.exclusive || 0) + (vis.unset || 0);
              const pct = total > 0 ? (v.count / total) * 100 : 0;
              return (
                <div key={v.key} className="flex items-center gap-3">
                  <v.icon className="h-3.5 w-3.5 text-[#888] shrink-0" />
                  <span className="text-[10px] font-semibold text-[#444] w-28 truncate">{v.label}</span>
                  <div className="flex-1 h-6 rounded-full bg-cream border border-cream-warm overflow-hidden">
                    <motion.div initial={{ width: 0 }} animate={{ width: `${Math.max(pct, 2)}%` }}
                      className={`h-full rounded-full ${v.color} opacity-70`} />
                  </div>
                  <span className="text-xs font-bold text-[#444] w-6 text-right">{v.count}</span>
                </div>
              );
            })}
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <div className="rounded-xl bg-cream border border-cream-warm p-3 text-center">
              <div className="text-lg font-bold text-emerald-600">{mp.deals_distributed}</div>
              <div className="text-[9px] text-[#999] uppercase tracking-wider">Distributed</div>
            </div>
            <div className="rounded-xl bg-cream border border-cream-warm p-3 text-center">
              <div className="text-lg font-bold text-gold-dark">{mp.funded_count}</div>
              <div className="text-[9px] text-[#999] uppercase tracking-wider">Funded</div>
            </div>
          </div>
        </div>
      </div>

      {/* Row 3: Top Lenders Table */}
      {topLenders.length > 0 && (
        <div className="rounded-xl border border-cream-warm bg-white p-5" data-testid="mp-top-lenders">
          <h4 className="text-xs font-bold text-[#444] uppercase tracking-wider mb-4 flex items-center gap-2">
            <Users className="h-4 w-4 text-gold-dark" /> Lender Leaderboard
          </h4>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-cream-warm text-[10px] uppercase tracking-wider text-[#999]">
                  <th className="text-left py-2 pr-3">#</th>
                  <th className="text-left py-2 pr-3">Lender</th>
                  <th className="text-center py-2 px-2">Tier</th>
                  <th className="text-center py-2 px-2">Score</th>
                  <th className="text-center py-2 px-2">Boxes</th>
                  <th className="text-center py-2 px-2">Quotes</th>
                  <th className="text-center py-2 px-2">Quote Rate</th>
                  <th className="text-center py-2 px-2">Resp Rate</th>
                </tr>
              </thead>
              <tbody>
                {topLenders.map((l, i) => (
                  <tr key={l.user_id} className="border-b border-cream-warm/50 hover:bg-cream/50 transition-colors">
                    <td className="py-2.5 pr-3 font-bold text-[#999]">{i + 1}</td>
                    <td className="py-2.5 pr-3 font-medium text-[#333] truncate max-w-[150px]">{l.name}</td>
                    <td className="py-2.5 px-2 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold border ${tierBadge(l.tier)}`}>
                        {l.tier === "preferred" ? "Preferred" : l.tier === "active" ? "Active" : "Standard"}
                      </span>
                    </td>
                    <td className="py-2.5 px-2 text-center font-bold text-emerald-700">{l.reliability_score}</td>
                    <td className="py-2.5 px-2 text-center text-[#666]">{l.boxes}</td>
                    <td className="py-2.5 px-2 text-center font-medium text-purple-600">{l.term_sheets}</td>
                    <td className="py-2.5 px-2 text-center text-[#666]">{(l.quote_rate * 100).toFixed(0)}%</td>
                    <td className="py-2.5 px-2 text-center text-[#666]">{(l.response_rate * 100).toFixed(0)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
